/**
 * Caption (burned-lyrics) styling — CLIENT side.
 *
 * A caption look is a full CaptionConfig (font/effects controls), not just a
 * preset id: presets are starting points that fill the config, and the Font /
 * Effects tabs edit it. The client always persists the RESOLVED config to
 * project metadata (captionConfig); the server compiles it to an ASS subtitle
 * script burned at export (server/captionStyles.ts). Font families here map
 * to OFL/Apache TTFs bundled in server/fonts — keep both lists in sync.
 */
import type { CSSProperties } from "react";

export type CaptionStyleId = string;
export type CaptionPosition = "top" | "middle" | "bottom";
export type CaptionAnimation =
  | "none"
  | "karaoke"
  | "bounce"
  | "pop"
  | "scale"
  | "slide-left"
  | "slide-up"
  | "underline"
  | "box"
  | "highlighter";

/**
 * Graphics layer burned with the lyrics ("hud" = Lookbook HUD: corner
 * brackets, LOOK labels, song title, REC timecode, film grain, left-aligned
 * lyrics). Separate from `preset` so font edits keep it.
 */
export type CaptionOverlay = "none" | "hud";

export type CaptionConfig = {
  /** Preset the config started from ("custom" once edited). */
  preset: string;
  fontFamily: string;
  /** Pixel size on a 720p-height canvas. */
  fontSizePx: number;
  fontWeight: 400 | 500 | 600 | 700 | 800 | 900;
  color: string;
  italic: boolean;
  underline: boolean;
  uppercase: boolean;
  strokeEnabled: boolean;
  strokeWidthPx: number;
  strokeColor: string;
  shadowEnabled: boolean;
  shadowX: number;
  shadowY: number;
  shadowBlur: number;
  shadowColor: string;
  position: CaptionPosition;
  animation: CaptionAnimation;
  /** Karaoke word fill / highlighter box color. */
  highlightColor: string;
  overlay: CaptionOverlay;
};

/** Families with a matching bundled TTF for the server-side burn. */
export const CAPTION_FONT_FAMILIES = [
  "Montserrat",
  "Anton",
  "Archivo Black",
  "Bebas Neue",
  "Comic Neue",
  "Permanent Marker",
  "Lora",
  "Oswald",
] as const;

export const CAPTION_WEIGHT_OPTIONS: Array<{
  value: CaptionConfig["fontWeight"];
  label: string;
}> = [
  { value: 400, label: "Normal" },
  { value: 500, label: "Medium" },
  { value: 600, label: "SemiBold" },
  { value: 700, label: "Bold" },
  { value: 800, label: "ExtraBold" },
  { value: 900, label: "Black" },
];

export const CAPTION_ANIMATION_OPTIONS: Array<{
  value: CaptionAnimation;
  label: string;
}> = [
  { value: "none", label: "None" },
  { value: "karaoke", label: "Karaoke fill" },
  { value: "bounce", label: "Bounce" },
  { value: "pop", label: "Pop" },
  { value: "scale", label: "Scale" },
  { value: "slide-left", label: "Slide Left" },
  { value: "slide-up", label: "Slide Up" },
  { value: "underline", label: "Underline" },
  { value: "box", label: "Box" },
  { value: "highlighter", label: "Highlighter Box" },
];

const baseConfig: Omit<CaptionConfig, "preset"> = {
  fontFamily: "Montserrat",
  fontSizePx: 40,
  fontWeight: 800,
  color: "#ffffff",
  italic: false,
  underline: false,
  uppercase: false,
  strokeEnabled: false,
  strokeWidthPx: 6,
  strokeColor: "#000000",
  shadowEnabled: true,
  shadowX: 2,
  shadowY: 2,
  shadowBlur: 2,
  shadowColor: "#000000",
  position: "bottom",
  animation: "none",
  highlightColor: "#a78bfa",
  overlay: "none",
};

export type CaptionStylePreset = {
  id: CaptionStyleId;
  label: string;
  description: string;
  /** null = captions off. */
  config: CaptionConfig | null;
};

const preset = (
  id: string,
  label: string,
  description: string,
  overrides: Partial<CaptionConfig>,
): CaptionStylePreset => ({
  id,
  label,
  description,
  config: { ...baseConfig, preset: id, ...overrides },
});

export const CAPTION_STYLE_PRESETS: CaptionStylePreset[] = [
  {
    id: "none",
    label: "No Lyrics",
    description: "No captions burned into the video",
    config: null,
  },
  preset("karaoke", "Karaoke", "Words light up as they are sung", {
    fontWeight: 900,
    fontSizePx: 32,
    uppercase: true,
    strokeEnabled: true,
    strokeWidthPx: 6,
    animation: "karaoke",
  }),
  preset("beasty", "Beasty", "Marker handwriting with a soft glow", {
    fontFamily: "Permanent Marker",
    fontSizePx: 46,
    fontWeight: 400,
    shadowX: 0,
    shadowY: 0,
    shadowBlur: 10,
    shadowColor: "#ffffff",
    animation: "pop",
  }),
  preset("deep-diver", "Deep Diver", "Plain, clean and steady", {
    fontWeight: 600,
    fontSizePx: 38,
    uppercase: true,
    shadowBlur: 3,
  }),
  preset("youshaei", "Youshaei", "Muted gray with a dark edge", {
    fontWeight: 800,
    fontSizePx: 42,
    uppercase: true,
    color: "#9ca3af",
    strokeEnabled: true,
    strokeWidthPx: 5,
    strokeColor: "#1f2937",
  }),
  preset("pod-p", "Pod P", "Two-tone pink karaoke fill", {
    fontWeight: 800,
    fontSizePx: 42,
    uppercase: true,
    animation: "karaoke",
    highlightColor: "#ff2ec4",
    strokeEnabled: true,
    strokeWidthPx: 4,
  }),
  preset("bold", "Bold", "Heavy uppercase with a strong outline", {
    fontFamily: "Archivo Black",
    fontWeight: 900,
    fontSizePx: 46,
    uppercase: true,
    strokeEnabled: true,
    strokeWidthPx: 6,
  }),
  preset("neon", "Neon", "Glowing pink, music-video energy", {
    fontFamily: "Bebas Neue",
    fontWeight: 700,
    fontSizePx: 48,
    color: "#ff4df0",
    shadowX: 0,
    shadowY: 0,
    shadowBlur: 12,
    shadowColor: "#ff4df0",
  }),
  preset("minimal", "Minimal", "Clean text on a soft dark box", {
    fontWeight: 600,
    fontSizePx: 30,
    shadowEnabled: false,
    animation: "box",
  }),
  preset("serif", "Serif", "Elegant, cinematic titles", {
    fontFamily: "Lora",
    fontWeight: 500,
    fontSizePx: 40,
    shadowBlur: 3,
  }),
  preset("subtle", "Subtle", "Small and unobtrusive at the bottom", {
    fontWeight: 500,
    fontSizePx: 24,
  }),
  preset(
    "lookbook-hud",
    "Lookbook HUD",
    "Giant lyrics with a camera HUD, look labels and film grain",
    {
      fontFamily: "Archivo Black",
      fontWeight: 900,
      fontSizePx: 60,
      uppercase: true,
      shadowX: 3,
      shadowY: 3,
      shadowBlur: 2,
      overlay: "hud",
    },
  ),
];

export function captionPresetById(id: unknown): CaptionStylePreset | undefined {
  return CAPTION_STYLE_PRESETS.find((item) => item.id === id);
}

export function normalizeCaptionStyleId(value: unknown): CaptionStyleId {
  if (value === "custom") return "custom";
  return captionPresetById(value)?.id ?? "none";
}

export function captionConfigLabel(
  styleId: string,
  config?: CaptionConfig | null,
): string {
  const fromPreset = captionPresetById(styleId)?.label;
  if (fromPreset && styleId !== "none") return fromPreset;
  if (styleId === "custom" || config) return "Custom";
  return "Off";
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const clampNum = (value: unknown, min: number, max: number, dflt: number) => {
  const num = Number(value);
  return Number.isFinite(num) ? Math.min(max, Math.max(min, num)) : dflt;
};

/** Parse a persisted config (unknown JSON) into a safe CaptionConfig. */
export function normalizeCaptionConfig(raw: unknown): CaptionConfig | null {
  if (!raw || typeof raw !== "object") return null;
  const value = raw as Record<string, unknown>;
  const hex = (input: unknown, dflt: string) =>
    typeof input === "string" && HEX_RE.test(input) ? input : dflt;
  const weight = CAPTION_WEIGHT_OPTIONS.some(
    (w) => w.value === value.fontWeight,
  )
    ? (value.fontWeight as CaptionConfig["fontWeight"])
    : 800;
  const family = CAPTION_FONT_FAMILIES.includes(
    value.fontFamily as (typeof CAPTION_FONT_FAMILIES)[number],
  )
    ? (value.fontFamily as string)
    : "Montserrat";
  const position: CaptionPosition =
    value.position === "top" || value.position === "middle"
      ? value.position
      : "bottom";
  const animation = CAPTION_ANIMATION_OPTIONS.some(
    (a) => a.value === value.animation,
  )
    ? (value.animation as CaptionAnimation)
    : "none";
  return {
    preset: typeof value.preset === "string" ? value.preset : "custom",
    fontFamily: family,
    fontSizePx: clampNum(value.fontSizePx, 12, 96, 40),
    fontWeight: weight,
    color: hex(value.color, "#ffffff"),
    italic: value.italic === true,
    underline: value.underline === true,
    uppercase: value.uppercase === true,
    strokeEnabled: value.strokeEnabled === true,
    strokeWidthPx: clampNum(value.strokeWidthPx, 1, 20, 6),
    strokeColor: hex(value.strokeColor, "#000000"),
    shadowEnabled: value.shadowEnabled === true,
    shadowX: clampNum(value.shadowX, -12, 12, 2),
    shadowY: clampNum(value.shadowY, -12, 12, 2),
    shadowBlur: clampNum(value.shadowBlur, 0, 24, 2),
    shadowColor: hex(value.shadowColor, "#000000"),
    position,
    animation,
    highlightColor: hex(value.highlightColor, "#a78bfa"),
    overlay: value.overlay === "hud" ? "hud" : "none",
  };
}

const FONT_CSS_FALLBACKS: Record<string, string> = {
  Montserrat: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
  Anton: "'Anton', Impact, 'Arial Black', sans-serif",
  "Archivo Black": "'Archivo Black', 'Arial Black', sans-serif",
  "Bebas Neue": "'Bebas Neue', 'Oswald', Impact, sans-serif",
  "Comic Neue": "'Comic Neue', 'Comic Sans MS', cursive",
  "Permanent Marker": "'Permanent Marker', 'Marker Felt', cursive",
  Lora: "'Lora', Georgia, 'Times New Roman', serif",
  Oswald: "'Oswald', 'Arial Narrow', sans-serif",
};

/** CSS for the live preview line (scale = preview px per 720p canvas px). */
export function cssForCaptionConfig(
  config: CaptionConfig,
  scale = 1,
): CSSProperties {
  const shadows: string[] = [];
  if (config.strokeEnabled) {
    const w = Math.max(1, Math.round(config.strokeWidthPx * scale * 0.5));
    for (const [dx, dy] of [
      [w, w],
      [-w, -w],
      [w, -w],
      [-w, w],
      [w, 0],
      [-w, 0],
      [0, w],
      [0, -w],
    ]) {
      shadows.push(`${dx}px ${dy}px 0 ${config.strokeColor}`);
    }
  }
  if (config.shadowEnabled) {
    shadows.push(
      `${Math.round(config.shadowX * scale)}px ${Math.round(config.shadowY * scale)}px ${Math.round(config.shadowBlur * scale)}px ${config.shadowColor}`,
    );
  }
  const css: CSSProperties = {
    fontFamily: FONT_CSS_FALLBACKS[config.fontFamily] || config.fontFamily,
    fontSize: Math.max(8, Math.round(config.fontSizePx * scale)),
    fontWeight: config.fontWeight,
    fontStyle: config.italic ? "italic" : "normal",
    textDecoration:
      config.underline || config.animation === "underline"
        ? "underline"
        : undefined,
    color: config.color,
    lineHeight: config.overlay === "hud" ? 1.08 : 1.25,
    textAlign: config.overlay === "hud" ? "left" : "center",
    textShadow: shadows.length ? shadows.join(", ") : undefined,
  };
  if (config.animation === "box") {
    css.background = "rgba(0,0,0,0.45)";
    css.padding = "0.22em 0.6em";
    css.borderRadius = 8;
  } else if (config.animation === "highlighter") {
    css.background = config.highlightColor;
    css.padding = "0.14em 0.45em";
    css.borderRadius = 6;
    css.boxDecorationBreak = "clone" as CSSProperties["boxDecorationBreak"];
  }
  return css;
}

/** Google Fonts stylesheet for the preview families (loaded on modal open). */
export const CAPTION_FONTS_STYLESHEET_URL =
  "https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,400..900;1,400..900&family=Anton&family=Archivo+Black&family=Bebas+Neue&family=Comic+Neue:wght@700&family=Permanent+Marker&family=Lora:ital,wght@0,400..700;1,400..700&family=Oswald:wght@400..700&display=swap";

/** Bundled sample video for the preview when the project has no video yet. */
export const CAPTION_SAMPLE_VIDEO_URL =
  "/product-tour/render-timeline/paper-boats.mp4";

/**
 * Real transcribed lyrics of the bundled Paper Boats sample video
 * (whisper-timestamped), so the preview shows genuine caption timing.
 */
export const CAPTION_SAMPLE_LYRICS: Array<{
  start: number;
  end: number;
  text: string;
}> = [
  { start: 0.4, end: 4.4, text: "Mama on the porch with a book in her lap" },
  {
    start: 4.6,
    end: 9.4,
    text: "Daddy hollered suppertime, we never came back",
  },
  {
    start: 9.6,
    end: 14.4,
    text: "We ran behind the oak where the old fence leans",
  },
  {
    start: 14.6,
    end: 21.4,
    text: "Spilling all our wishes through the ink on the seams",
  },
  { start: 21.6, end: 26.4, text: "Paper boats, paper boats" },
  {
    start: 26.6,
    end: 31.8,
    text: "Carry me slow down where the summer and the streetlights go",
  },
  {
    start: 32.1,
    end: 34.4,
    text: "Every little hope and every little tear",
  },
  {
    start: 34.6,
    end: 36.9,
    text: "We trusted to the water and the river to hear",
  },
  { start: 37.1, end: 39.6, text: "Paper boats" },
];

/** Length of the sample-lyrics loop (matches the Paper Boats clip). */
export const CAPTION_SAMPLE_DURATION_SEC = 40;
